import type { PayoutBatchesTranslations } from "@/shared/lang/interfaces/payoutBatches";

import type { SelfApprovalReason } from "../../types/payoutBatches.types";

type GuideKey = keyof PayoutBatchesTranslations;

export type GuideScene = "route" | "template" | "upload" | "review" | "approval" | "self_approval" | "run";

export interface GuideStep {
  /** The page stepper's phase the step belongs to, so the guide speaks the same words as the screens. */
  phase: GuideKey;
  title: GuideKey;
  body: GuideKey;
  scene: GuideScene;
  /** The step about who approves, which points to where the organization decides it. */
  aboutApproval?: boolean;
}

const SELF_APPROVAL_BODY_KEYS: Record<SelfApprovalReason, GuideKey> = {
  org_setting: "guide_self_approval_body_org_setting",
  sole_approver: "guide_self_approval_body_sole_approver",
};

/**
 * The life of a batch, always in six steps. Who approves it is the viewer's own: a member who may approve
 * what they prepare reads that they do, and why; anyone else reads that another person does.
 */
export function batchGuideSteps(selfApproval: SelfApprovalReason | null): GuideStep[] {
  return [
    { phase: "step_configure", title: "guide_route_title", body: "guide_route_body", scene: "route" },
    {
      phase: "step_configure",
      title: "guide_template_title",
      body: "guide_template_body",
      scene: "template",
    },
    { phase: "step_configure", title: "guide_upload_title", body: "guide_upload_body", scene: "upload" },
    {
      phase: "step_review",
      title: "guide_review_title",
      body: selfApproval ? "guide_review_body_self" : "guide_review_body",
      scene: "review",
    },
    selfApproval
      ? {
          phase: "step_approval",
          title: "guide_self_approval_title",
          body: SELF_APPROVAL_BODY_KEYS[selfApproval],
          scene: "self_approval",
          aboutApproval: true,
        }
      : {
          phase: "step_approval",
          title: "guide_approval_title",
          body: "guide_approval_body",
          scene: "approval",
          aboutApproval: true,
        },
    { phase: "step_confirm", title: "guide_run_title", body: "guide_run_body", scene: "run" },
  ];
}
